
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';

interface Activity {
  id: string;
  type: string;
  title: string;
  description: string;
  poId?: string | number;
  poNumber?: string;
  createdAt: string;
  metadata?: any;
}

@Component
export default class ActivityFeed extends Vue {
  @Prop({ default: () => [] }) activities!: Activity[];
  @Prop({ default: false }) loading!: boolean;
  @Prop({ default: true }) hasMore!: boolean;

  loadingMore = false;
  visibleCount = 10;
  observer: IntersectionObserver | null = null;

  get visibleActivities() {
    return this.activities.slice(0, this.visibleCount);
  }

  mounted() {
    this.setupInfiniteScroll();
  }

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  setupInfiniteScroll() {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting && this.hasMore && !this.loadingMore) {
        this.loadMore();
      }
    }, options);

    if (this.$refs.loadMoreTrigger) {
      this.observer.observe(this.$refs.loadMoreTrigger as Element);
    }
  }

  @Watch('activities')
  onActivitiesChange() {
    // Reset visible count when activities change (e.g., after filter)
    this.visibleCount = 10;
  }

  getActivityIcon(type: string): string {
    const icons: Record<string, string> = {
      created: 'mdi-file-plus',
      sent: 'mdi-email-send',
      confirmed: 'mdi-check-circle',
      received: 'mdi-package-variant',
      partial_received: 'mdi-package-variant-closed',
      canceled: 'mdi-close-circle',
      automation: 'mdi-robot',
      approval: 'mdi-check-decagram',
      approval_rejected: 'mdi-close-decagram',
      error: 'mdi-alert-circle'
    };
    return icons[type] || 'mdi-information';
  }

  getActivityColor(type: string): string {
    const colors: Record<string, string> = {
      created: 'blue',
      sent: 'indigo',
      confirmed: 'green',
      received: 'teal',
      partial_received: 'orange',
      canceled: 'red',
      automation: 'purple',
      approval: 'green',
      approval_rejected: 'red',
      error: 'red'
    };
    return colors[type] || 'grey';
  }

  formatTime(dateString: string): string {
    const date = new Date(dateString);
    
    if (isToday(date)) {
      return `Today at ${format(date, 'h:mm a')}`;
    } else if (isYesterday(date)) {
      return `Yesterday at ${format(date, 'h:mm a')}`;
    } else {
      const distance = formatDistanceToNow(date, { addSuffix: true });
      // If more than 7 days, show the actual date
      if (distance.includes('month') || distance.includes('year')) {
        return format(date, 'MMM d, yyyy');
      }
      return distance;
    }
  }

  handleItemClick(activity: Activity) {
    if (activity.poId) {
      const poId = typeof activity.poId === 'string' ? parseInt(activity.poId) : activity.poId;
      this.$emit('item-click', poId);
    }
  }

  async loadMore() {
    if (this.loadingMore || !this.hasMore) return;
    
    this.loadingMore = true;
    
    // If we're showing all local activities, request more from server
    if (this.visibleCount >= this.activities.length) {
      this.$emit('load-more');
    } else {
      // Otherwise just show more of what we have
      this.visibleCount += 10;
    }
    
    // Simulate loading delay for better UX
    setTimeout(() => {
      this.loadingMore = false;
    }, 500);
  }
}
